// Pure logic for the TRIAGE_V2 arrival screen (no React, no DOM), so it can be unit tested.
import type { Programme } from '@/types';
import type { ProgressState } from '@/services/progress';
import { currentModule } from '@/services/contentProvider';

export type TriageTileId = 'unsafe' | 'triggered' | 'ex' | 'programme' | 'compassion' | 'notsure';
export type TriageRoute = 'home' | 'triggered' | 'ex' | 'programme' | 'compassion';

export type TriageTarget =
  | { kind: 'crisis' }
  | { kind: 'ask' }
  | { kind: 'route'; route: Exclude<TriageRoute, 'home'> };

export const TRIAGE_TILE_ORDER: TriageTileId[] = ['unsafe', 'triggered', 'ex', 'programme', 'compassion', 'notsure'];

/** What tapping a tile does. Crisis and Ask are overlays; the rest open a route home. */
export function tileTarget(id: TriageTileId): TriageTarget {
  switch (id) {
    case 'unsafe': return { kind: 'crisis' };
    case 'notsure': return { kind: 'ask' };
    default: return { kind: 'route', route: id };
  }
}

/** Crisis support is never remembered as the last choice. */
export function isRememberable(id: TriageTileId): boolean {
  return id !== 'unsafe';
}

// ── "Last time" chip: the last tile chosen, remembered for the calendar day ──────────
export const LAST_KEY = 'rn_triage_last';
export const dayKeyOf = (d: Date = new Date()): string => d.toDateString();

interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void; }

export function loadLastChoice(storage: StorageLike | null | undefined, day: string = dayKeyOf()): TriageTileId | null {
  try {
    const raw = storage?.getItem(LAST_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { id?: string; day?: string };
    if (v.day !== day) return null;
    const id = v.id as TriageTileId;
    return TRIAGE_TILE_ORDER.includes(id) && isRememberable(id) ? id : null;
  } catch {
    return null;
  }
}

export function saveLastChoice(storage: StorageLike | null | undefined, id: TriageTileId, day: string = dayKeyOf()): void {
  if (!isRememberable(id)) return;
  try { storage?.setItem(LAST_KEY, JSON.stringify({ id, day })); } catch { /* storage unavailable */ }
}

// ── Programme progress line: "Chapter N of M · lesson x of y" ───────────────────────
export function programmeProgressLine(programme: Programme, state: ProgressState): string | null {
  if (!programme.modules.length) return null;
  const mod = currentModule(programme, state);
  const chapterNo = programme.modules.findIndex((m) => m.id === mod.id) + 1;
  const total = mod.steps.length;
  if (chapterNo < 1 || total < 1) return null;
  const done = mod.steps.filter((s) => state.completedSteps.includes(s.id)).length;
  const lessonNo = Math.min(done + 1, total);
  return `Chapter ${chapterNo} of ${programme.modules.length} · lesson ${lessonNo} of ${total}`;
}

// ── Which triage screen is showing ──────────────────────────────────────────────────
// Owned by App's Shell (not TriageHome): App keys the screen on the active programme, so
// choosing Recovery programme or Self-compassion remounts TriageHome in the same render
// that would record the choice. Shell survives that remount; TriageHome is controlled.
// 'meditationsFromTriggered' is the same list opened from the triggered page, so the origin
// travels in the view itself (and is cleared by every reset to 'home').
export type TriageView = TriageRoute | 'meditations' | 'meditationsFromTriggered' | 'programmeFromCompassion';

/** Where Back from the meditations list goes: the page it was opened from. */
export function meditationsBackTarget(view: TriageView): TriageView {
  return view === 'meditationsFromTriggered' ? 'triggered' : 'home';
}

/** Where Back from the Recovery programme goes: the Self-compassion page if it was opened from there. */
export function programmeBackTarget(view: TriageView): TriageView {
  return view === 'programmeFromCompassion' ? 'compassion' : 'home';
}

// ── "Waiting on you": top two by priority ───────────────────────────────────────────
export const WAITING_PRIORITY = ['checkin', 'fogs', 'care', 'actsurvey', 'chapterfeedback', 'reminders', 'trybreathing'];

export function topWaiting<T extends { key: string }>(items: T[], max = 2): T[] {
  const rank = (k: string) => { const i = WAITING_PRIORITY.indexOf(k); return i < 0 ? WAITING_PRIORITY.length : i; };
  return [...items].sort((a, b) => rank(a.key) - rank(b.key)).slice(0, max);
}
